import { CustomerDetailsService } from 'src/app/angularJS/services/customer-detail/customer-detail.service';
import { PhotoService } from 'src/app/angularJS/services/photo/photo.service';
import { PaymentService } from 'src/app/angularJS/services/payments/payments.service';
import { BreadcrumbService } from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';
import { ChargeAccountService } from 'src/app/angularJS/services/charge-account/charge-account.service';
import { LinkBuilder } from 'src/app/angularJS/services/link-builder.service';
import { UserService } from 'src/app/angularJS/services/user/user.service';
import { QueryBuilder } from 'src/app/angularJS/services/query-builder.service';
import { PrimaryAction } from 'src/app/angularJS/layout/primary_action_button/primary_action_button.service';

/**
 * A controller used to view the details of a customer
 */
export class CustomerDetailController {
  public static $inject = [
    '$state',
    'customer',
    'customerDetailService',
    'photoService',
    '$window',
    'customerConstants',
    'paymentService',
    'breadcrumbService',
    'chargeAccountService',
    'LinkBuilder',
    'userService',
    'PrimaryAction',
    'QueryBuilder'
  ];
  public enhancedDetails: any;
  public image: any;
  public customer: InterACT.Interfaces.Customers.Customer;
  public paymentTokens: Array<InterACT.Interfaces.Payments.PaymentToken>;
  public paymentTokenActiveIndex: number;
  public tokenTypeList: any;
  public chargeAccounts: Array<InterACT.Interfaces.ChargeAccounts.ChargeAccount>;
  public hasChargeAccountAccess: boolean;

  constructor(
    private $state: ng.ui.IStateService,
    customer: InterACT.Interfaces.Customers.Customer,
    private customerDetailService: CustomerDetailsService,
    private photoService: PhotoService,
    private $window: ng.IWindowService,
    customerConstants,
    private paymentService: PaymentService,
    private breadcrumbService: BreadcrumbService,
    private chargeAccountService: ChargeAccountService,
    private linkBuilder: LinkBuilder,
    private userService: UserService,
    private primaryAction: PrimaryAction,
    private queryBuilder: QueryBuilder
  ) {
    this.customer = angular.copy(customer);
    this.enhancedDetails = {};
    this.paymentTokenActiveIndex = undefined;
    this.tokenTypeList = customerConstants.TOKEN_TYPES;
    this.activate();
  }

  /**
   * A method which is used to set the indicator to show payment card details
   */
  public togglePanel(index: number) {
    if (this.paymentTokenActiveIndex !== index) {
      this.paymentTokenActiveIndex = index;
    } else {
      this.paymentTokenActiveIndex = undefined;
    }
  }

  /**
   * A method which is used to get a customers photo
   */
  public getPhoto = () => {
    this.photoService.getCustomerPhoto(this.customer.id, this.customer.currentPhotoId).then((response) => {
      const blob = new Blob([response]);

      //ISSUE: [https://github.com/weareact/interACT/issues/934]
      // This is a temporary issue relating to phantomJs 2.5 exploding when attempting to create the image here
      //PhantomJs 2.5 is currently in beta so this should be revisted when we we move to the released version
      try {
        this.image = (this.$window.URL || (<any>this.$window).webkitURL).createObjectURL(blob);
      } catch (err) {
        // console.log('An issue occurred creating the image: ' + err);
      }
    });
  }

  /**
   * A method which is used for the primary action button to take the user to the edit screen.
   */
  public goToEdit = () => {
    this.$state.go('customer.edit', { customer: this.customer, customerId: this.customer.id });
  }

  public formatNumber(contactNumber: string) {
    return `${contactNumber.slice(0, 5)} ${contactNumber.slice(5)}`;
  }

  /**
   * A method to return a formatted string based on whether to get the type or value of the contact object
   * @param valueToGet a string based on whether to the type of contact or the value of the contact
   */
  public getFormattedPrimary(valueToGet: string) {
    if (this.enhancedDetails.contactDetails.primaryEmail) {
      return valueToGet === 'type' ? this.enhancedDetails.contactDetails.primaryEmail.type.toLowerCase() : this.enhancedDetails.contactDetails.primaryEmail.detail;
    }
    const formattedNumber = this.formatNumber(this.enhancedDetails.contactDetails.primaryContactMethod.detail);
    return valueToGet === 'type' ? this.enhancedDetails.contactDetails.primaryContactMethod.type.toLowerCase() : formattedNumber;
  }

  /**
   * Method to get the current balance of the charge account object as it may not exist
   * @param chargeAccount the charge account object
   */
  public getCurrentBalanceValue = (chargeAccount: InterACT.Interfaces.ChargeAccounts.ChargeAccount) => {
    return (chargeAccount.creditBalance) ? chargeAccount.creditBalance : 0;
  }

  /**
   * A method which is used to instantiate the controller
   */
  private activate() {
    this.hasChargeAccountAccess = this.userService.permissions.ui &&
      this.userService.permissions.ui['charge-account'] &&
      this.userService.permissions.ui['charge-account'].canAccess;

    if (this.customer.id) {
      if (this.hasChargeAccountAccess) {
        this.getPaymentTokens();
        this.getChargeAccounts();
      }
      if (this.customer.currentPhotoId) {
        this.getPhoto();
      }

      this.enhancedDetails = this.customerDetailService.getEnhancedCustomerData(this.customer);
      const fullName = this.enhancedDetails && this.enhancedDetails.fullName ? this.enhancedDetails.fullName : 'CUSTOMER_TABS.ANONYMOUS';
      this.breadcrumbService.setBreadcrumbs([
        {
          name: 'SEARCH',
          sref: 'customers'
        }, {
          name: fullName,
          sref: 'customer.details',
          options: {
            reload: 'customer'
          }
        }, {
          name: 'CUSTOMER_TABS.DETAILS',
          sref: 'customer.details'
        }
      ]);

      if (this.userService.permissions.ui.customers.canUpdate) {
        this.primaryAction.setActions([{
          name: 'Edit customer',
          icon: 'mode_edit',
          action: this.goToEdit
        }]);
      }
    }
  }

  /**
   * Call payment token api to get already registered Payment Cards.
   */
  private getPaymentTokens = () => {
    const param = 'customer.link=' + '/customers/' + this.customer.id;
    this.paymentService.getPaymentTokens(undefined, undefined, param).then((paymentTokens: Array<InterACT.Interfaces.Payments.PaymentToken>) => {
      this.paymentTokens = paymentTokens;
    });
  }

  private getChargeAccounts = () => {
    this.chargeAccounts = [];
    const schemeLink = this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id);
    const chargeAccountsQuery: string = this.queryBuilder
      .newParam()
      .fieldIs('customer.link', this.linkBuilder.getCustomersLink(this.customer.id))
      .and()
      .fieldIs('scheme.link', schemeLink)
      .and()
      .not()
      .fieldIs('status', 'DELETED')
      .getParam();
    this.chargeAccountService.getChargeAccounts(1, 10, chargeAccountsQuery).then((chargeAccounts: Array<InterACT.Interfaces.ChargeAccounts.ChargeAccount>) => {
      this.chargeAccounts = chargeAccounts;
    });
  }
}
